import { forwardRef, useState, useRef, type InputHTMLAttributes, type ChangeEvent } from 'react';
import { Minus, Plus } from 'lucide-react';

export type NumberInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  label?: string;
  error?: string;
  showStepper?: boolean;
  onIncrement?: () => void;
  onDecrement?: () => void;
};

export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  ({ label, error, showStepper = true, onIncrement, onDecrement, className = '', id, disabled, min, max, step = 1, value, defaultValue, onChange, ...props }, ref) => {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');
    const internalRef = useRef<HTMLInputElement>(null);
    const inputRef = (ref as React.RefObject<HTMLInputElement>) || internalRef;

    // Track internal value for uncontrolled mode
    const [internalValue, setInternalValue] = useState<number>(
      defaultValue !== undefined ? Number(defaultValue) : 0
    );

    const isControlled = value !== undefined;
    const currentValue = isControlled ? Number(value) : internalValue;

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value === '' ? 0 : Number(e.target.value);
      if (!isControlled) {
        setInternalValue(newValue);
      }
      onChange?.(e);
    };

    const updateValue = (newValue: number) => {
      if (isControlled) {
        // For controlled mode, dispatch a synthetic event
        if (inputRef.current) {
          const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
            window.HTMLInputElement.prototype,
            'value'
          )?.set;
          nativeInputValueSetter?.call(inputRef.current, newValue);
          inputRef.current.dispatchEvent(new Event('input', { bubbles: true }));
        }
      } else {
        setInternalValue(newValue);
      }
    };

    const handleIncrement = () => {
      if (onIncrement) {
        onIncrement();
      } else {
        const maxValue = max !== undefined ? Number(max) : Infinity;
        const newValue = Math.min(currentValue + Number(step), maxValue);
        updateValue(newValue);
      }
    };

    const handleDecrement = () => {
      if (onDecrement) {
        onDecrement();
      } else {
        const minValue = min !== undefined ? Number(min) : -Infinity;
        const newValue = Math.max(currentValue - Number(step), minValue);
        updateValue(newValue);
      }
    };

    const isDecrementDisabled = disabled || (min !== undefined && currentValue <= Number(min));
    const isIncrementDisabled = disabled || (max !== undefined && currentValue >= Number(max));

    return (
      <div className={`flex flex-col gap-1.5 ${className}`}>
        {label && (
          <label htmlFor={inputId} className="text-sm font-medium text-gray-300">
            {label}
          </label>
        )}
        <div className="relative flex items-center">
          {showStepper && (
            <button
              type="button"
              onClick={handleDecrement}
              disabled={isDecrementDisabled}
              className={`
                absolute left-0 h-full px-2.5 flex items-center justify-center
                text-gray-400 hover:text-white transition-colors
                disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-gray-400
                rounded-l-lg hover:bg-surface-700
              `}
              tabIndex={-1}
            >
              <Minus className="w-4 h-4" />
            </button>
          )}
          <input
            ref={inputRef}
            id={inputId}
            type="number"
            min={min}
            max={max}
            step={step}
            value={currentValue}
            onChange={handleChange}
            disabled={disabled}
            className={`
              w-full py-2 rounded-lg bg-surface-800 border text-white text-center tabular-nums
              placeholder-gray-500 transition-colors duration-200
              focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent
              disabled:opacity-50 disabled:cursor-not-allowed
              ${showStepper ? 'px-8' : 'px-4'}
              ${error ? 'border-red-500' : 'border-surface-700 hover:border-surface-600'}
              [appearance:textfield]
              [&::-webkit-outer-spin-button]:appearance-none
              [&::-webkit-inner-spin-button]:appearance-none
            `}
            {...props}
          />
          {showStepper && (
            <button
              type="button"
              onClick={handleIncrement}
              disabled={isIncrementDisabled}
              className={`
                absolute right-0 h-full px-2.5 flex items-center justify-center
                text-gray-400 hover:text-white transition-colors
                disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-gray-400
                rounded-r-lg hover:bg-surface-700
              `}
              tabIndex={-1}
            >
              <Plus className="w-4 h-4" />
            </button>
          )}
        </div>
        {error && <p className="text-sm text-red-500">{error}</p>}
      </div>
    );
  }
);

NumberInput.displayName = 'NumberInput';
